import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  User, Globe, Instagram, Facebook, Linkedin, Mail, 
  Award, Download, Video, BookOpen
} from 'lucide-react';
import { XIcon } from '../../components/icons/XIcon';
import { Logo } from '../../components/Logo';
import { QRPageFooter } from '../../components/qr/QRPageFooter';
import { PhoneMockup } from '../../components/PhoneMockup';

const AuthorDemo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const isDemo = new URLSearchParams(location.search).get('demo') === 'true';
  
  useEffect(() => {
    if (isDemo) {
      window.history.replaceState({}, '', '/qr/demo-author');
    }
  }, [isDemo]);

  const handleBackToDemos = () => {
    navigate('/');
    setTimeout(() => {
      const demosSection = document.getElementById('demos');
      if (demosSection) {
        const sectionIndex = 7; // Demos is the 8th section (0-indexed)
        window.scrollTo({
          top: sectionIndex * window.innerHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Data for Johann Wolfgang von Goethe
  const authorData = {
    name: 'Johann Wolfgang von Goethe',
    shortBio: 'Deutscher Dichter, Naturforscher und Staatsmann',
    fullBio: `Johann Wolfgang von Goethe (1749-1832) war einer der bedeutendsten deutschen Dichter und Denker. Als Hauptvertreter des Sturm und Drang sowie der Weimarer Klassik prägte er die deutsche Literatur nachhaltig.

Seine Werke, darunter "Faust", "Die Leiden des jungen Werthers" und "Wilhelm Meisters Lehrjahre", gehören zu den wichtigsten der Weltliteratur. Neben seiner literarischen Tätigkeit war Goethe auch als Naturwissenschaftler tätig und verfasste bedeutende Arbeiten zur Farbenlehre und Morphologie.

Als Staatsmann wirkte er am Hof von Weimar und prägte das kulturelle Leben seiner Zeit maßgeblich.`,
    profileImageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Goethe_%28Stieler_1828%29.jpg/440px-Goethe_%28Stieler_1828%29.jpg',
    awards: [
      'Geadelt durch Kaiser Joseph II. (1782)',
      'Mitglied der Leopoldina (1818)',
      'Ehrenbürger der Stadt Frankfurt (1824)'
    ],
    books: [
      { title: 'Faust I & II', year: '1808/1832' },
      { title: 'Die Leiden des jungen Werthers', year: '1774' },
      { title: 'Wilhelm Meisters Lehrjahre', year: '1795' },
      { title: 'Italienische Reise', year: '1816' }
    ]
  };

  const brandingData = {
    companyName: 'Max Mustermann GmbH',
    logoUrl: null,
    primaryColor: '#4F46E5',
    displayType: 'name_only' as const
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          {isDemo && (
            <button
              onClick={handleBackToDemos}
              className="mb-4 text-blue-600 hover:text-blue-700 transition-colors text-sm"
            >
              ← Zurück zu Demos
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Autorenprofil Demo</h1>
          <p className="text-gray-600">So sieht Ihr Autorenprofil auf dem Smartphone aus</p>
        </div>

        {/* Phone Mockup with Author Profile */}
        <PhoneMockup className="mb-8">
          <div className="min-h-screen bg-gray-50">
            {/* Header with Branding */}
            <div className="bg-white shadow-sm">
              <div className="container mx-auto px-4 py-4">
                <div className="flex items-center justify-center">
                  <span className="text-lg font-semibold" style={{ color: brandingData.primaryColor }}>
                    {brandingData.companyName}
                  </span>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-6">
              {/* Profile Header */}
              <div className="bg-white rounded-2xl shadow-xl p-6 mb-4">
                <div className="flex flex-col items-center text-center">
                  {authorData.profileImageUrl ? (
                    <img
                      src={authorData.profileImageUrl}
                      alt={authorData.name}
                      className="w-24 h-24 rounded-full object-cover shadow-lg mb-4"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-indigo-100 flex items-center justify-center shadow-lg mb-4">
                      <User className="w-12 h-12 text-indigo-600" />
                    </div>
                  )}
                  
                  <h1 className="text-xl font-bold text-gray-900 mb-1">{authorData.name}</h1>
                  <p className="text-sm text-gray-600 mb-3">{authorData.shortBio}</p>
                  
                  {/* Social Links */}
                  <div className="flex gap-2">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Globe className="w-4 h-4" />
                    </div>
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Instagram className="w-4 h-4" />
                    </div>
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Facebook className="w-4 h-4" />
                    </div>
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <XIcon className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Biography */}
              <div className="bg-white rounded-2xl shadow-xl p-6 mb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Biografie</h2>
                <div className="text-sm text-gray-700">
                  {authorData.fullBio.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="mb-3">{paragraph}</p>
                  ))}
                </div>
              </div>

              {/* Awards */}
              <div className="bg-white rounded-2xl shadow-xl p-6 mb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-500" />
                  Auszeichnungen & Ehrungen
                </h2>
                <ul className="space-y-2">
                  {authorData.awards.map((award, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <span className="text-yellow-500 mt-0.5">•</span>
                      <span className="text-gray-700">{award}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Selected Works */}
              <div className="bg-white rounded-2xl shadow-xl p-6 mb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Ausgewählte Werke
                </h2>
                <div className="space-y-2">
                  {authorData.books.map((book, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <h3 className="font-medium text-sm text-gray-900">{book.title}</h3>
                      <p className="text-xs text-gray-600">{book.year}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact & Actions */}
              <div className="bg-white rounded-2xl shadow-xl p-6 mb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Kontakt</h2>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-gray-700 text-sm">
                    <Mail className="w-4 h-4" />
                    <span>E-Mail senden</span>
                  </div>
                  
                  <div className="text-sm text-gray-700">
                    <strong>Literaturagent:</strong> Max Mustermann Verlag
                  </div>

                  <button className="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
                    Newsletter abonnieren
                  </button>
                </div>
              </div>

              {/* Footer */}
              <QRPageFooter 
                branding={{ primaryColor: brandingData.primaryColor }} 
                className="text-gray-600 text-xs" 
              />
            </div>
          </div>
        </PhoneMockup>

        {/* Demo Info */}
        {isDemo && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <h3 className="text-lg font-semibold mb-2 text-blue-900">Demo-Hinweis</h3>
              <p className="text-sm text-blue-800 mb-2">
                Dies ist eine Demo eines Autorenprofils mit dem Branding "Max Mustermann GmbH". 
                Die Seite zeigt Johann Wolfgang von Goethe als Beispielautor.
              </p>
              <p className="text-sm text-blue-800">
                Mit BeyondPages können Sie professionelle Autorenprofile erstellen, die über QR-Codes 
                in Ihren Büchern erreichbar sind. Das Profil kann jederzeit aktualisiert werden, 
                auch nach dem Druck des Buches.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorDemo;